import { notifications } from '@mantine/notifications';
import { useMutation, useQuery } from '@tanstack/react-query';
import { MRT_ColumnFiltersState, MRT_SortingState } from 'mantine-react-table';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { contractsApi } from '../../lib/apis/contracts';

import { FORM_STATUS, useSharedQueries } from '@data-access';

type UseValidationFormsExportParams = {
  filters: MRT_ColumnFiltersState;
  sorting: MRT_SortingState;
};

export function useValidationFormsExport({
  filters,
  sorting,
}: UseValidationFormsExportParams) {
  const { t } = useTranslation();
  const sharedQueries = useSharedQueries();
  const [computationRequestId, setComputationRequestId] = useState<
    number | null
  >(null);
  const hasDownloadedRef = useRef(false);

  const prepExport = useMutation({
    mutationFn: contractsApi.validationForms.prepExport,
    onSuccess: (data) => {
      hasDownloadedRef.current = false;
      setComputationRequestId(data.instance_id);
    },
    onError: () => {
      notifications.show({
        title: t('common.ERROR'),
        message: t('common.error.pageError'),
        color: 'red',
      });
    },
  });

  const downloadExport = useMutation({
    mutationFn: contractsApi.validationForms.downloadExport,
    onError: () => {
      notifications.show({
        title: t('common.ERROR'),
        message: t('common.error.pageError'),
        color: 'red',
      });
    },
  });

  const computationRequest = useQuery({
    ...sharedQueries.computationRequest({ id: computationRequestId! }),
    enabled: computationRequestId !== null,
  });

  useEffect(() => {
    if (
      computationRequest.data?.status === FORM_STATUS.FINISHED &&
      !hasDownloadedRef.current &&
      !downloadExport.isPending
    ) {
      hasDownloadedRef.current = true;
      downloadExport.mutate({ id: computationRequest.data.id });
      setComputationRequestId(null);
    } else if (computationRequest.data?.status === FORM_STATUS.ERROR) {
      notifications.show({
        title: t('common.ERROR'),
        message: t('common.error.pageError'),
        color: 'red',
      });
      setComputationRequestId(null);
    }
  }, [computationRequest.data, downloadExport, t]);

  const handleExport = () => {
    prepExport.mutate({ filters, sorting });
  };

  const isExportLoading =
    prepExport.isPending ||
    downloadExport.isPending ||
    computationRequestId !== null ||
    computationRequest.data?.status === FORM_STATUS.PENDING;

  return {
    handleExport,
    isExportLoading,
  };
}
